import { useAddress } from '@/store/selector';
import { formatDisplay } from '@/utils/format-display';
import BigNumber from 'bignumber.js';
import { useMemo } from 'react';
import useSWR from 'swr';

export interface Portfolio {
  walletAddress: string;
  rankUser: number | null;
  tradingVolume: string | null;
  avgLeverage: string | null;
  totalTrades: number | null;
  winRate: string | null;
  totalPnl: string | null;
  totalPnlBeforeFee: string | null;
  totalPnlAfterFee: string | null;
  email: string | null;
}

const DEFAULT_PORTFOLIO = {
  walletAddress: '--',
  rankUser: '--',
  tradingVolume: '--',
  avgLeverage: '--',
  totalTrades: '--',
  winRate: '--',
  totalPnl: '--',
  totalPnlBeforeFee: '--',
  totalPnlAfterFee: '--',
  winTotalTrade: '--',
};

// --- Raw shape returned by the new dexhistory stats API ---
interface DexHistoryStats {
  funding_unknown_count: number;
  lifetime_pnl: string;
  meta: {
    decimals: {
      usdc_decimals: number;
      price_decimals: number;
    };
  };
  pairs: {
    collateral_unknown_count: number;
    pair_index: number;
    pnl: string;
    total_collateral: string;
    trade_count: number;
  }[];
  rollover_unknown_count: number;
  total_funding_fee: string;
  total_rollover_fee: string;
  total_volume: string;
  trade_count: number;
  trader: string;
  volume_unknown_count: number;
  win_count: number;
  win_rate: number;
  win_unknown_count: number;
}

function mapDexHistoryStats(stats: DexHistoryStats): Portfolio {
  const totalPnl = (Number(stats.lifetime_pnl) / Math.pow(10, 18)).toString();

  return {
    walletAddress: stats.trader,
    rankUser: null,
    tradingVolume: (Number(stats.total_volume) / Math.pow(10, 18)).toString(),
    avgLeverage: null,
    totalTrades: stats.trade_count,
    winRate: (stats.win_rate * 100).toString(),
    totalPnl,
    totalPnlBeforeFee: null,
    totalPnlAfterFee: totalPnl,
    email: null,
  };
}

async function call(url: string) {
  const res = await fetch(url);

  const stats: DexHistoryStats = await res.json();

  return mapDexHistoryStats(stats);
}

export function usePortfolios() {
  const address = useAddress();

  const { data, error, isLoading } = useSWR<Portfolio>(
    address
      ? `https://mhga.holdstation.com/dexhistory/wallets/${address}/stats?chain=480`
      : null,
    call
  );
  const dataDisplay = useMemo(() => {
    if (!address) {
      return DEFAULT_PORTFOLIO;
    }

    return {
      ...data,
      totalPnl: formatDisplay(new BigNumber(data?.totalPnl ?? 0).toNumber(), {
        decimalToShow: 2,
      }),
      totalPnlAfterFee: formatDisplay(
        new BigNumber(data?.totalPnlAfterFee ?? 0).toNumber(),
        { decimalToShow: 2 }
      ),
      tradingVolume: formatDisplay(
        new BigNumber(data?.tradingVolume ?? 0).toNumber(),
        { decimalToShow: 2 }
      ),
      winTotalTrade: Math.round(
        (Number(data?.winRate ?? 0) / 100) * Number(data?.totalTrades ?? 0)
      ),
    };
  }, [address, data]);

  return {
    data,
    error,
    dataDisplay,
    isLoading,
  };
}
